// Morrow — visual workflow builder canvas (React Flow). A systematic set of building
// blocks on an infinite pan/zoom canvas: TRIGGER (what starts it) → APP (pick an app,
// then a sequence of actions: do this, then that) / BROWSER (what to do on the web) →
// OUTPUT (what comes out — a message, a summary, anything). Add blocks from the left
// rail; they auto-chain. Bezier edges. The graph saves on the workflow.
import {
  addEdge,
  Background,
  BackgroundVariant,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  Handle,
  MarkerType,
  NodeResizer,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useStore,
  type Connection,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  ArrowUpRightIcon,
  ClockIcon,
  CloudIcon,
  CpuIcon,
  DatabaseIcon,
  FolderIcon,
  GitBranchIcon,
  LayoutGridIcon,
  PlusIcon,
  RepeatIcon,
  RotateCwIcon,
  SearchIcon,
  SmartphoneIcon,
  Trash2Icon,
  XIcon,
  ZapIcon,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { apiUrl } from "../../apiBase";
import { fetchVaultFolders } from "../../morrowVault";
import { useI18n, type Translator } from "~/i18n";

import { canvasKindLabel, defaultNodeData, outputChannels } from "./workflowCanvasI18n";
import { loopEdges } from "./workflowGraphText";
import {
  conditionSummary,
  conditionValue,
  defaultLoopRoutes,
  emptyCondition,
  hasRoutes,
  nodeAppSlugs,
  ROUTE_CONDITION_KINDS,
  routeIdFromLabel,
  withConditionValue,
  type WfRoute,
  type WfRouteConditionKind,
} from "./workflowRoutes";

export type WfNodeKind =
  | "trigger"
  | "app"
  | "browser"
  | "cloudBrowser"
  | "computer"
  | "vault"
  | "decision"
  | "foreach"
  | "output";
export type WfForEachBodyKind = "browser" | "app" | "ai";
/** A For each block's settings, as saved in the graph — the engine's parseForEachConfig reads
 * them (workflowForEach.ts). Blank strings mean "not set". */
export type WfForEachConfig = {
  source?:
    | { kind: "sheet"; spreadsheetId?: string; sheet?: string }
    | { kind: "step"; stepId?: string };
  filter?: { column?: string; notEquals?: string };
  concurrency?: number;
  staggerSeconds?: number;
  onFail?: "skip" | "stop";
  body?: { kind?: WfForEachBodyKind; text?: string };
  saveTo?: { outcomeColumn?: string; noteColumn?: string };
  dryRun?: boolean;
};
const FOREACH_DEFAULT_AT_ONCE = 3;
const FOREACH_DEFAULT_STAGGER = 20;
export type WfNodeData = {
  kind: WfNodeKind;
  label: string;
  appSlug?: string;
  /** App block: the ordered actions to do in that app ("do this, then that"). */
  steps?: string[];
  /** Browser block: what to do on the web. Output/Decision block: the rule/output text. */
  text?: string;
  /** Vault block: ids of the specific files this workflow may read. */
  vaultIds?: string[];
  /** Vault block: a vault FOLDER id — "newest image from this folder" mode (vaultIds stays empty). */
  folder?: string;
  /** Send/output block: which channel to reply on — a comms channel or "personal-whatsapp". */
  channel?: string;
  /** Autonomous: keep working the target (e.g. keep replying) instead of a one-shot. */
  autonomous?: boolean;
  /** Per-node instruction (the engine reads `instruction || text`). New edits will target
   * this once the inspector lands; Phase A still writes the legacy `text` so both the
   * per-node engine and the compiled-blob fallback stay in sync. */
  instruction?: string;
  /** Per-node model id override. Absent = kind default (decision → gate classifier,
   * task → the turn's default). Persisted for the engine to consume; display-only today. */
  model?: string;
  /** End node: what happens when the flow finishes. "message" (default) sends on `channel`;
   * "save" writes the result to the Vault; "webhook" POSTs it to `webhookUrl`; "none" just
   * finishes with no message (fixes the "keeps messaging" default). */
  endMode?: "message" | "save" | "webhook" | "none";
  /** End node, webhook mode: the URL to POST the result to. */
  webhookUrl?: string;
  /** Superseded by a decision node's `routes` — a route pointed back at an earlier step is how a
   * loop is drawn now. Still read so an older saved graph can be migrated, never written. */
  loopBack?: boolean;
  /** Decision node: the named routes it chooses between, one per output port. Exactly one fires.
   * Absent = the old single-exit gate that can only stop the flow. */
  routes?: WfRoute[];
  /** Decision node with a route that loops back: how many times round before it gives up and
   * reports what it managed. */
  maxPasses?: number;
  /** App step: every app this step may use. Supersedes the single `appSlug`, which is still read
   * so graphs saved before this keep working. */
  appSlugs?: string[];
  /** App step: needs ALL the listed apps ("all", the default) or any ONE of them ("any"). */
  appMode?: "all" | "any";
  /** For-each: run this step once PER ITEM of the incoming list (its primary dependency's
   * output). Persisted into the saved graph; the engine reads it in workflowGraphPlan. */
  forEach?: boolean;
  /** For-each: the node id whose output is the list to iterate. Set only when the node has 2+
   * data sources and the author picked one; absent = the first source. Read by the engine. */
  forEachSource?: string;
  /** Named output fields this step declares (e.g. name, price, id). A downstream step references
   * them as {{ThisLabel.field}}; the engine asks this step to end its reply with a JSON object of
   * exactly these keys so they're parseable. Persisted into the saved graph. */
  outputs?: string[];
  /** For each v1 (self-contained): the list, the per-item step, how many at once, where results go.
   * Absent on a v2 container (whose body is child nodes and whose list comes from the node before). */
  forEachConfig?: WfForEachConfig;
  /** For each v2 container settings (read by the engine for the parallel-map). */
  concurrency?: number;
  staggerSeconds?: number;
  onFail?: "skip" | "stop";
  dryRun?: boolean;
  /** Start block: what a run needs before it may begin — ["image"] = an attached image. */
  requires?: string[];
};
export type WfGraph = { nodes: Node<WfNodeData>[]; edges: Edge[] };

/** A connected app the user can pick in a step. `personal` marks the Baileys personal
 * WhatsApp (which is not a Composio toolkit) so a send-step can target it distinctly. */
export type WfApp = { slug: string; name: string; personal?: boolean };

const AppsContext = createContext<WfApp[]>([]);
const VaultContext = createContext<ReadonlyArray<{ id: string; name: string }>>([]);
const EditTriggersContext = createContext<() => void>(() => {});
/** Insert a node of `kind` in the middle of edge `edgeId` (between source & target). */
const InsertContext = createContext<
  (edgeId: string, source: string, target: string, kind: WfNodeKind) => void
>(() => {});

// React Flow renders custom EDGE components through its own internal store, and React
// Context to them proved unreliable here — the "+ App/Browser/Decision" click silently
// no-op'd (context resolved to the default empty fn). Keep a module-level handle that the
// canvas sets every render and the edge reads AT CLICK TIME, so it's always the current
// insert fn regardless of context. (One canvas is mounted at a time, so a module singleton
// is safe.)
type InsertFn = (edgeId: string, source: string, target: string, kind: WfNodeKind) => void;
let activeInsert: InsertFn = () => {};
function prettyApp(slug: string): string {
  return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : slug;
}
/** Composio logo URL for a slug; the personal-WhatsApp pseudo-app borrows WhatsApp's. */
function logoFor(slug: string): string {
  return `https://logos.composio.dev/api/${slug === "personal-whatsapp" ? "whatsapp" : slug}`;
}

/** A connection with a "+" at its midpoint — click to insert a step (App / Browser)
 * between the two nodes; it auto-rewires. */
function InsertEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  style,
  data,
}: EdgeProps) {
  const { t } = useI18n();
  const allEdges = useStore((st) => st.edges);
  // A route pointing BACK upstream is the loop. A bezier for it cuts straight through the node
  // cards between the two ends, which is why a drawn loop looked like a glitch; a stepped path
  // routed below the row reads as "and round again".
  const isLoop = useMemo(() => loopEdges(allEdges).some((e) => e.id === id), [allEdges, id]);
  const [path, labelX, labelY] = isLoop
    ? getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        borderRadius: 16,
        offset: 60,
      })
    : getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
      });
  const [open, setOpen] = useState(false);
  // A `dependsOn` edge means "fetch the source's data and feed it to the target BEFORE the
  // target runs" (e.g. a vault node → a decision node). The runtime honors this at run time.
  const { setEdges } = useReactFlow();
  const dependsOn = (data as { dependsOn?: boolean } | undefined)?.dependsOn === true;
  const toggleDepends = () =>
    setEdges((es) =>
      es.map((e) => (e.id === id ? { ...e, data: { ...e.data, dependsOn: !dependsOn } } : e)),
    );
  // There was no way to undo a connection at all — you could draw one and then had to delete a
  // whole node to get rid of it.
  const removeEdge = () => setEdges((es) => es.filter((e) => e.id !== id));
  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        {...(markerEnd ? { markerEnd } : {})}
        style={
          isLoop
            ? { ...style, stroke: "#f59e0b", strokeWidth: 2 }
            : dependsOn
              ? { ...style, stroke: "var(--primary)", strokeDasharray: "5 4", strokeWidth: 1.75 }
              : style
        }
      />
      <EdgeLabelRenderer>
        <div
          className="nodrag nopan absolute"
          style={{
            transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: "all",
            // Keep an OPEN menu above every other edge label as well as the nodes.
            zIndex: open ? 20 : 1,
          }}
        >
          {!open && isLoop ? (
            <span className="pointer-events-none mb-0.5 block whitespace-nowrap rounded bg-amber-500/15 px-1 text-[9px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
              {t("workflows.canvas.loopsBack")}
            </span>
          ) : null}
          {open ? (
            <div className="flex items-center gap-1 rounded-lg border border-border bg-background p-1 shadow-lg">
              <button
                type="button"
                onClick={() => {
                  activeInsert(id, source, target, "app");
                  setOpen(false);
                }}
                className="rounded-md px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted"
              >
                {t("workflows.canvas.insertApp")}
              </button>
              <button
                type="button"
                onClick={() => {
                  activeInsert(id, source, target, "browser");
                  setOpen(false);
                }}
                className="rounded-md px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted"
              >
                {t("workflows.canvas.insertBrowser")}
              </button>
              <button
                type="button"
                onClick={() => {
                  activeInsert(id, source, target, "decision");
                  setOpen(false);
                }}
                className="rounded-md px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted"
              >
                {t("workflows.canvas.insertDecision")}
              </button>
              <button
                type="button"
                onClick={() => {
                  toggleDepends();
                  setOpen(false);
                }}
                title={t("workflows.canvas.dependsHint")}
                className={`rounded-md px-2 py-0.5 text-[11px] font-medium hover:bg-muted ${
                  dependsOn ? "text-primary" : "text-foreground"
                }`}
              >
                {dependsOn ? t("workflows.canvas.feedsDataOn") : t("workflows.canvas.feedsData")}
              </button>
              <button
                type="button"
                onClick={() => {
                  removeEdge();
                  setOpen(false);
                }}
                title={t("workflows.canvas.removeEdgeHint")}
                className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted hover:text-red-500"
              >
                <Trash2Icon className="size-3" />
                {t("workflows.canvas.removeEdge")}
              </button>
              <button
                type="button"
                aria-label={t("workflows.canvas.close")}
                onClick={() => setOpen(false)}
                className="rounded-md px-1 py-0.5 text-muted-foreground hover:text-foreground"
              >
                <XIcon className="size-3" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setOpen(true)}
              title={
                dependsOn
                  ? t("workflows.canvas.dependsEdgeTitle")
                  : t("workflows.canvas.insertTitle")
              }
              className={`flex size-5 items-center justify-center rounded-full border bg-background shadow transition hover:opacity-100 ${
                dependsOn
                  ? "border-primary text-primary opacity-100"
                  : "border-border text-foreground opacity-70"
              }`}
            >
              <PlusIcon className="size-3" />
            </button>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
const EDGE_TYPES = { insert: InsertEdge };

const ACCENT: Record<WfNodeKind, { dot: string; chip: string; icon: ReactNode; tint: string }> = {
  trigger: {
    dot: "!bg-emerald-500",
    chip: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400",
    tint: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    icon: <ZapIcon className="size-3.5" />,
  },
  app: {
    dot: "!bg-blue-500",
    chip: "bg-blue-500/12 text-blue-600 dark:text-blue-400",
    tint: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    icon: <LayoutGridIcon className="size-3.5" />,
  },
  browser: {
    dot: "!bg-violet-500",
    chip: "bg-violet-500/12 text-violet-600 dark:text-violet-400",
    tint: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    icon: <SmartphoneIcon className="size-3.5" />,
  },
  cloudBrowser: {
    dot: "!bg-cyan-500",
    chip: "bg-cyan-500/12 text-cyan-600 dark:text-cyan-400",
    tint: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
    icon: <CloudIcon className="size-3.5" />,
  },
  computer: {
    dot: "!bg-indigo-500",
    chip: "bg-indigo-500/12 text-indigo-600 dark:text-indigo-400",
    tint: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
    icon: <CpuIcon className="size-3.5" />,
  },
  vault: {
    dot: "!bg-teal-500",
    chip: "bg-teal-500/12 text-teal-600 dark:text-teal-400",
    tint: "bg-teal-500/10 text-teal-600 dark:text-teal-400",
    icon: <FolderIcon className="size-3.5" />,
  },
  decision: {
    dot: "!bg-rose-500",
    chip: "bg-rose-500/12 text-rose-600 dark:text-rose-400",
    tint: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    icon: <GitBranchIcon className="size-3.5" />,
  },
  foreach: {
    dot: "!bg-orange-500",
    chip: "bg-orange-500/12 text-orange-600 dark:text-orange-400",
    tint: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
    icon: <RepeatIcon className="size-3.5" />,
  },
  output: {
    dot: "!bg-amber-500",
    chip: "bg-amber-500/12 text-amber-600 dark:text-amber-400",
    tint: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    icon: <ArrowUpRightIcon className="size-3.5" />,
  },
};

// No overflow-hidden: the connector handles (esp. the bottom depends handle) sit on the card
// edge and must not be clipped. The spine rounds its own left corners to match the card instead.
const CARD =
  "relative w-64 rounded-xl border border-border/70 bg-card shadow-[0_1px_2px_rgba(0,0,0,0.05),0_8px_22px_-16px_rgba(0,0,0,0.28)]";
const FIELD =
  "nodrag w-full rounded-lg border border-border bg-background px-2 py-1.5 text-[12px] text-foreground outline-none placeholder:text-muted-foreground/50 focus:border-primary/50";
/** Quiet full-width action button, for the inspector's own add/enable actions. */
const BTN_SOFT =
  "nodrag inline-flex w-full items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-[11px] font-semibold text-foreground transition hover:bg-muted";

/**
 * A textarea that reliably grows with its content (JS-measured, so it doesn't depend on
 * `field-sizing:content` being supported in the host) up to `maxPx`, then scrolls. This
 * fixes the cramped node fields — they now expand as you type and scroll when very long,
 * instead of clipping to a couple of lines. */
function AutoTextarea({
  value,
  onChange,
  placeholder,
  className,
  minPx = 40,
  maxPx = 360,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  minPx?: number;
  maxPx?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, minPx), maxPx)}px`;
  }, [value, minPx, maxPx]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={1}
      className={`${FIELD} resize-none overflow-y-auto leading-snug ${className ?? ""}`}
      style={{ minHeight: minPx, maxHeight: maxPx }}
    />
  );
}

/** The cheap classifier the decision gate runs on (engine default MORROW_WORKFLOW_GATE_MODEL).
 * A task node whose model is set to this runs on the fast/cheap model instead of the full one;
 * absent `data.model` = the kind default (decision → gate, task → the workflow's model). */
const GATE_MODEL = "@cf/zai-org/glm-5.3-flash";

/**
 * How the engine assigns nodes to AGENT TURNS — drives the "same agent" colour-coding.
 *  - "perNode" (LIVE today, default via MORROW_WORKFLOW_NODE_EXEC_FULL): each work/output node
 *              runs its OWN isolated agent turn — it sees only its labeled context (its sources'
 *              outputs), not a shared conversation. So each node = its own agent.
 *  - "blob"    (legacy fallback, MORROW_WORKFLOW_NODE_EXEC_FULL=0): EVERY work/output node
 *              collapses into ONE shared agent turn (all task nodes = Agent 1).
 * Kept as a single switch so the UI never lies about which steps actually share an agent. */
const AGENT_EXEC_MODE: "blob" | "perNode" = "perNode";
/** Distinct dot/text colours for agent groups (cycled by index) — same colour = same agent. */
const AGENT_COLORS = [
  "bg-indigo-500",
  "bg-fuchsia-500",
  "bg-lime-500",
  "bg-orange-500",
  "bg-sky-500",
  "bg-pink-500",
] as const;

/** Solid per-kind spine colour — the thin left bar that carries kind identity, so the
 * connector handles no longer need to shout it with big coloured dots. */
const SPINE: Record<WfNodeKind, string> = {
  trigger: "bg-emerald-500",
  app: "bg-blue-500",
  browser: "bg-violet-500",
  cloudBrowser: "bg-cyan-500",
  computer: "bg-indigo-500",
  vault: "bg-teal-500",
  decision: "bg-rose-500",
  foreach: "bg-orange-500",
  output: "bg-amber-500",
};

/**
 * Which model runs a node, expressed for the reader — forward-compatible with the
 * per-node engine (decision = the cheap classifier gate; app/browser/output = the full
 * model turn). Trigger/vault aren't agent turns, so they carry no model badge. This is
 * display-only for now (the actual per-node model key is persisted in Phase B once the
 * engine's field shape is confirmed). */
type NodeModel = { tone: "fast" | "full"; label: string; title: string };
function nodeModel(kind: WfNodeKind, t: Translator, routing = false): NodeModel | null {
  // "Gate" is only true of a ROUTE-LESS decision: that one is the relevance classifier the engine
  // runs ahead of the costly work. A decision WITH routes runs in its own place in the flow, so
  // calling it a gate claimed an execution order the engine never used.
  if (kind === "decision")
    return {
      tone: "fast",
      label: t(routing ? "workflows.canvas.modelQuick" : "workflows.canvas.modelGate"),
      title: "glm-5.3-flash",
    };
  if (
    kind === "app" ||
    kind === "browser" ||
    kind === "cloudBrowser" ||
    kind === "computer" ||
    kind === "foreach" ||
    kind === "output"
  )
    return {
      tone: "full",
      label: t("workflows.canvas.modelFull"),
      title: t("workflows.canvas.modelFullTitle"),
    };
  return null;
}

/** A node's flow-derived facts, reactive to edge changes:
 *  - `usesFrom`: labels of source nodes this node has a `dependsOn` (data) edge from — an
 *    EXPLICIT data source whose output is fed in before this node runs. */
function useNodeFlowFacts(id: string): { usesFrom: string[] } {
  const edges = useStore((s) => s.edges);
  const { getNode } = useReactFlow();
  return useMemo(() => {
    const incoming = edges.filter((e) => e.target === id);
    const deps = incoming.filter((e) => (e.data as { dependsOn?: boolean } | undefined)?.dependsOn);
    const usesFrom = deps
      .map((e) => (getNode(e.source)?.data as WfNodeData | undefined)?.label?.trim() || "")
      .filter((l) => l.length > 0);
    return { usesFrom };
  }, [edges, id, getNode]);
}

/**
 * The agent group a node belongs to (drives the "same agent" colour) — or null when the node
 * runs no agent turn of its own (trigger/vault) or is the gate (decision, shown separately).
 * Reactive to node changes. In "blob" mode every work/output node is Agent 1 (they share one
 * turn today); in "perNode" mode each gets its own number/colour. */
function useAgentGroup(id: string): { label: string; colorClass: string } | null {
  const nodes = useStore((s) => s.nodes);
  return useMemo(() => {
    const node = nodes.find((n) => n.id === id) as Node<WfNodeData> | undefined;
    if (!node) return null;
    const kind = node.data.kind;
    if (kind === "trigger" || kind === "vault" || kind === "decision") return null;
    const isWork = (k: WfNodeKind) =>
      k === "app" || k === "browser" || k === "cloudBrowser" || k === "computer" || k === "output";
    const workNodes = (nodes as Node<WfNodeData>[])
      .filter((n) => isWork(n.data.kind))
      .sort((a, b) => a.position.x - b.position.x || a.position.y - b.position.y);
    const index =
      AGENT_EXEC_MODE === "perNode"
        ? Math.max(
            0,
            workNodes.findIndex((n) => n.id === id),
          )
        : 0;
    return {
      label: String(index + 1),
      colorClass: AGENT_COLORS[index % AGENT_COLORS.length] ?? "bg-indigo-500",
    };
  }, [nodes, id]);
}

/**
 * What a decision node actually DOES — the two kinds behave nothing alike, and labelling both of
 * them "Runs first · cheap" is what made a run look like it had started at the if/else:
 *  - a decision with NO routes is the relevance gate: the engine runs it as a classifier BEFORE
 *    the expensive work, so an irrelevant run costs almost nothing. "Runs first" is true of it.
 *  - a decision WITH routes is a router: it runs in its own position in the flow, after the steps
 *    that feed it, and picks which route continues. Nothing about it runs first.
 * `loopsTo` is the label of the node a route points BACK to (`""` when that node is unnamed), or
 * null when no route loops — a loop is the single most consequential thing a router can do, so
 * the card says it rather than leaving it to be inferred from a line on the canvas.
 */
function useDecisionRole(id: string): { router: boolean; loopsTo: string | null } {
  const nodes = useStore((s) => s.nodes);
  const edges = useStore((s) => s.edges);
  return useMemo(() => {
    const node = nodes.find((n) => n.id === id) as Node<WfNodeData> | undefined;
    if (!hasRoutes(node?.data.routes)) return { router: false, loopsTo: null };
    for (const e of edges) {
      if (e.source !== id || !e.sourceHandle) continue;
      // A route whose target can reach this decision again by going forward is a back edge.
      if (!reachesForward(edges, e.target, id)) continue;
      const target = nodes.find((n) => n.id === e.target) as Node<WfNodeData> | undefined;
      return { router: true, loopsTo: target?.data.label?.trim() ?? "" };
    }
    return { router: true, loopsTo: null };
  }, [nodes, edges, id]);
}

type ChipTone = "uses" | "parallel" | "gate" | "loop";
const CHIP_TONE: Record<ChipTone, string> = {
  uses: "bg-teal-500/12 text-teal-600 dark:text-teal-400 border-teal-500/25",
  parallel: "bg-primary/10 text-primary border-primary/25",
  gate: "bg-rose-500/12 text-rose-600 dark:text-rose-400 border-rose-500/25",
  loop: "bg-amber-500/12 text-amber-600 dark:text-amber-400 border-amber-500/25",
};
function NodeChip({
  tone,
  icon,
  children,
}: {
  tone: ChipTone;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9.5px] font-semibold ${CHIP_TONE[tone]}`}
    >
      {icon}
      {children}
    </span>
  );
}

/** The small model badge shown top-right in a node header. */
function ModelBadge({ model }: { model: NodeModel }) {
  return (
    <span
      title={model.title}
      className="ml-auto inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-1.5 py-0.5 text-[9px] font-semibold text-muted-foreground"
    >
      <span
        className={`size-1.5 rounded-full ${model.tone === "fast" ? "bg-emerald-500" : "bg-primary"}`}
      />
      {model.label}
    </span>
  );
}

function NodeShell({
  kind,
  onDelete,
  hasIn,
  hasOut,
  outPorts,
  title,
  model,
  children,
}: {
  kind: WfNodeKind;
  onDelete?: () => void;
  hasIn: boolean;
  hasOut: boolean;
  /** Named output ports, one per route, instead of the single unnamed output. Each is its own
   * handle, so an edge remembers WHICH route it leaves by — and so "one of these" reads apart
   * from "all of these" without opening anything. */
  outPorts?: ReadonlyArray<{ readonly id: string; readonly label: string }>;
  title: ReactNode;
  /** Optional model badge (top-right) — which model runs this node. */
  model?: NodeModel | null;
  children?: ReactNode;
}) {
  const { t } = useI18n();
  const a = ACCENT[kind];
  // Handles are now neutral + small: kind identity lives in the left spine, not in a big
  // coloured dot, so the canvas reads calm instead of confetti.
  const handle = "!size-2.5 !border-2 !border-background !bg-muted-foreground/40";
  return (
    <div className={`group ${CARD}`}>
      <span className={`absolute inset-y-0 left-0 w-1 rounded-l-xl ${SPINE[kind]}`} aria-hidden />
      {hasIn ? <Handle type="target" position={Position.Left} className={handle} /> : null}
      <div className="flex items-center gap-2 pl-3.5 pr-3 pt-3">
        <span className={`flex size-6 items-center justify-center rounded-lg ${a.tint}`}>
          {a.icon}
        </span>
        <span
          className={`rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${a.chip}`}
        >
          {canvasKindLabel(kind, t)}
        </span>
        {model ? <ModelBadge model={model} /> : null}
        {onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            className={`${model ? "shrink-0" : "ml-auto"} text-muted-foreground/40 opacity-0 transition hover:text-red-500 group-hover:opacity-100`}
            aria-label={t("workflows.canvas.delete")}
          >
            <Trash2Icon className="size-3.5" />
          </button>
        ) : null}
      </div>
      <div className="pl-3.5 pr-3 pb-3 pt-2">
        {title}
        {children}
      </div>
      {outPorts && outPorts.length > 0 ? (
        <div className="absolute inset-y-0 right-0 w-0">
          {outPorts.map((port, i) => (
            <div
              key={port.id}
              className="absolute right-0"
              style={{ top: `${((i + 1) / (outPorts.length + 1)) * 100}%` }}
            >
              <Handle
                id={port.id}
                type="source"
                position={Position.Right}
                style={{ position: "relative", top: 0, right: 0, transform: "none" }}
                className="!size-2.5 !border-2 !border-background !bg-primary"
              />
              <span className="pointer-events-none absolute left-3 top-1/2 max-w-24 -translate-y-1/2 truncate text-[8.5px] font-semibold uppercase tracking-wide text-primary/80">
                {port.label}
              </span>
            </div>
          ))}
        </div>
      ) : hasOut ? (
        <Handle type="source" position={Position.Right} className={handle} />
      ) : null}
      {/* Bottom = DATA DEPENDENCIES: drag any node's output here to make this node depend on it
          (an edge with dependsOn:true). Multiple allowed. Teal to read as data, not control. */}
      {kind !== "trigger" ? (
        <>
          <Handle
            id="dep"
            type="target"
            position={Position.Bottom}
            title={t("workflows.canvas.dependsHandle")}
            className="!size-3 !border-2 !border-background !border-dashed !bg-teal-500"
          />
          <span className="pointer-events-none absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-[8px] font-semibold uppercase tracking-wide text-teal-600/70 dark:text-teal-400/70">
            {t("workflows.canvas.dependsHandleLabel")}
          </span>
        </>
      ) : null}
    </div>
  );
}

/** Footer chips that expose a node's flow role: which agent turn runs it, the decision gate, and
 * its explicit inbound data sources ("Uses: X"). Renders nothing when a node has no such facts,
 * so plain steps stay clean. */
function FlowChips({ id, kind }: { id: string; kind: WfNodeKind }) {
  const { t } = useI18n();
  const { usesFrom } = useNodeFlowFacts(id);
  const agent = useAgentGroup(id);
  const decision = useDecisionRole(id);
  const chips: ReactNode[] = [];
  // Which agent runs this node — same colour = same agent turn. Decisions show the gate chip
  // instead (they ARE their own cheap gate); trigger/vault run no turn.
  if (agent)
    chips.push(
      <span
        key="agent"
        title={t("workflows.canvas.agentHint")}
        className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-1.5 py-0.5 text-[9.5px] font-semibold text-muted-foreground"
      >
        <span className={`size-1.5 rounded-full ${agent.colorClass}`} />
        {t("workflows.canvas.chipAgent", { n: agent.label })}
      </span>,
    );
  if (kind === "decision") {
    chips.push(
      <NodeChip
        key="role"
        tone="gate"
        icon={
          decision.router ? (
            <GitBranchIcon className="size-2.5" />
          ) : (
            <ClockIcon className="size-2.5" />
          )
        }
      >
        {t(decision.router ? "workflows.canvas.chipRouter" : "workflows.canvas.chipGate")}
      </NodeChip>,
    );
    if (decision.loopsTo !== null)
      chips.push(
        <NodeChip key="loop" tone="loop" icon={<RepeatIcon className="size-2.5" />}>
          {t("workflows.canvas.chipLoopsBack", {
            target: decision.loopsTo || t("workflows.canvas.routes.loopsTo"),
          })}
        </NodeChip>,
      );
  }
  for (const src of usesFrom)
    chips.push(
      <NodeChip key={`uses-${src}`} tone="uses" icon={<DatabaseIcon className="size-2.5" />}>
        {t("workflows.canvas.chipUses", { source: src })}
      </NodeChip>,
    );
  if (chips.length === 0) return null;
  return <div className="mt-2 flex flex-wrap gap-1">{chips}</div>;
}

function usePatch(id: string) {
  const { setNodes } = useReactFlow();
  return useCallback(
    (p: Partial<WfNodeData>) =>
      setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...p } } : n))),
    [id, setNodes],
  );
}
function useDelete(id: string) {
  const { setNodes, setEdges } = useReactFlow();
  return useCallback(() => {
    setNodes((ns) => ns.filter((n) => n.id !== id));
    setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
  }, [id, setNodes, setEdges]);
}

function TriggerNode({ id, data }: NodeProps) {
  const d = data as WfNodeData;
  const { t } = useI18n();
  const patch = usePatch(id);
  const editTriggers = useContext(EditTriggersContext);
  // What the run must start with: an image, a document, or any attachment. Stored in
  // `requires` ("attachment" = any); "file" is accepted as a legacy alias for document.
  const req = d.requires ?? [];
  const attKind: "image" | "document" | "any" | "" = req.includes("image")
    ? "image"
    : req.includes("document") || req.includes("file")
      ? "document"
      : req.includes("attachment")
        ? "any"
        : "";
  const setAttKind = (kind: "image" | "document" | "any" | "") => {
    const base = req.filter((r) => !["image", "document", "file", "attachment"].includes(r));
    patch({ requires: kind === "" ? base : [...base, kind === "any" ? "attachment" : kind] });
  };
  return (
    <NodeShell
      kind="trigger"
      onDelete={useDelete(id)}
      hasIn={false}
      hasOut
      title={
        <div className="flex items-center justify-between gap-2">
          <div className="text-[13px] font-semibold text-foreground">{d.label}</div>
          <button
            type="button"
            onClick={editTriggers}
            title={t("workflows.canvas.editTriggers")}
            className="nodrag shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10"
          >
            {t("workflows.canvas.edit")}
          </button>
        </div>
      }
    >
      <div className="nodrag mt-2 flex flex-wrap items-center gap-2 text-[11px] text-foreground/80">
        <label className="flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={attKind !== ""}
            onChange={(e) => setAttKind(e.target.checked ? "any" : "")}
          />
          {t("workflows.canvas.trigger.needsAttachment")}
        </label>
        {attKind !== "" ? (
          <select
            className="rounded border border-border bg-background px-1.5 py-0.5 text-[11px] text-foreground outline-none focus:border-primary/50"
            value={attKind}
            onChange={(e) => setAttKind(e.target.value as "image" | "document" | "any")}
          >
            <option value="any">{t("workflows.canvas.trigger.att.any")}</option>
            <option value="image">{t("workflows.canvas.trigger.att.image")}</option>
            <option value="document">{t("workflows.canvas.trigger.att.document")}</option>
          </select>
        ) : null}
      </div>
    </NodeShell>
  );
}

/** Searchable app picker used inside a node: shows the user's connected apps first, and
 * searches the full Composio catalog (~1000 apps) when you type. Expands inline (a
 * scrollable list inside the card) so it never clips behind other nodes. */
function AppPicker({
  value,
  connected,
  onPick,
  addMore = false,
}: {
  value: string | undefined;
  connected: WfApp[];
  onPick: (app: WfApp) => void;
  /** Some apps are already chosen and listed below, so this is an ADD control, not an empty
   * slot — "Choose an app…" above a row of chosen apps reads as if nothing is set. */
  addMore?: boolean;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<WfApp[]>([]);
  const [loading, setLoading] = useState(false);

  const current =
    connected.find((a) => a.slug === value) ??
    (value ? { slug: value, name: prettyApp(value) } : null);

  // Search the full catalog when the user types; otherwise show connected apps.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setHits(connected);
      return;
    }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      void fetch(apiUrl(`/api/morrow/composio/toolkits?limit=24&q=${encodeURIComponent(term)}`), {
        headers: { accept: "application/json" },
      })
        .then((r) =>
          r.ok ? (r.json() as Promise<{ toolkits?: { slug: string; name: string }[] }>) : null,
        )
        .then((res) => {
          if (!alive) return;
          const list = (res?.toolkits ?? []).map((t) => ({ slug: t.slug, name: t.name }));
          // Keep connected matches on top.
          const connMatch = connected.filter(
            (a) =>
              a.name.toLowerCase().includes(term.toLowerCase()) ||
              a.slug.includes(term.toLowerCase()),
          );
          const seen = new Set(connMatch.map((a) => a.slug));
          setHits([...connMatch, ...list.filter((a) => !seen.has(a.slug))]);
        })
        .catch(() => alive && setHits(connected))
        .finally(() => alive && setLoading(false));
    }, 220);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, open, connected]);

  return (
    <div className="nodrag relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${FIELD} flex items-center gap-1.5 text-left font-medium`}
      >
        {current ? (
          <>
            <img
              src={logoFor(current.slug)}
              alt=""
              className="size-4 shrink-0 rounded object-contain"
              onError={(e) => ((e.currentTarget as HTMLImageElement).style.visibility = "hidden")}
            />
            <span className="truncate">{current.name}</span>
          </>
        ) : (
          <span className="text-muted-foreground/60">
            {addMore ? t("workflows.canvas.addAnotherApp") : t("workflows.canvas.chooseApp")}
          </span>
        )}
        {addMore && !current ? (
          <PlusIcon className="ml-auto size-3 shrink-0 text-muted-foreground/50" />
        ) : (
          <SearchIcon className="ml-auto size-3 shrink-0 text-muted-foreground/50" />
        )}
      </button>
      {open ? (
        <div className="mt-1 rounded-lg border border-border bg-background p-1 shadow-sm">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("workflows.canvas.searchApps")}
            className="mb-1 w-full rounded-md border border-border bg-muted/30 px-2 py-1 text-[12px] outline-none focus:border-primary/50"
          />
          <div className="flex max-h-48 flex-col gap-0.5 overflow-y-auto">
            {(q.trim().length < 2 ? connected : hits).map((a) => (
              <button
                key={a.slug}
                type="button"
                onClick={() => {
                  onPick(a);
                  setOpen(false);
                  setQ("");
                }}
                className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[12px] hover:bg-muted"
              >
                <img
                  src={logoFor(a.slug)}
                  alt=""
                  className="size-4 shrink-0 rounded object-contain"
                  onError={(e) =>
                    ((e.currentTarget as HTMLImageElement).style.visibility = "hidden")
                  }
                />
                <span className="truncate text-foreground/90">{a.name}</span>
                {a.personal ? (
                  <span className="ml-auto rounded bg-emerald-500/15 px-1 text-[9px] font-semibold text-emerald-600">
                    {t("workflows.canvas.badgePersonal")}
                  </span>
                ) : connected.some((c) => c.slug === a.slug) ? (
                  <span className="ml-auto rounded bg-blue-500/15 px-1 text-[9px] font-semibold text-blue-600">
                    {t("workflows.apps.connected")}
                  </span>
                ) : null}
              </button>
            ))}
            {loading ? (
              <div className="px-2 py-1 text-[11px] text-muted-foreground/60">
                {t("workflows.apps.searching")}
              </div>
            ) : (q.trim().length < 2 ? connected : hits).length === 0 ? (
              <div className="px-2 py-1 text-[11px] text-muted-foreground/60">
                {q.trim().length < 2
                  ? t("workflows.canvas.noConnectedApps")
                  : t("workflows.canvas.noMatches")}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AppNode({ id, data }: NodeProps) {
  const d = data as WfNodeData;
  const { t } = useI18n();
  const apps = useContext(AppsContext);
  const patch = usePatch(id);
  const steps = d.steps && d.steps.length > 0 ? d.steps : [""];

  const setStep = (i: number, v: string) => {
    const next = [...steps];
    next[i] = v;
    patch({ steps: next });
  };
  const addStep = () => patch({ steps: [...steps, ""] });
  const removeStep = (i: number) =>
    patch({
      steps: steps.filter((_, j) => j !== i).length ? steps.filter((_, j) => j !== i) : [""],
    });

  return (
    <NodeShell
      kind="app"
      model={nodeModel("app", t)}
      onDelete={useDelete(id)}
      hasIn
      hasOut
      title={
        <div className="mb-2 flex flex-col gap-1">
          <AppPicker
            value={undefined}
            addMore={nodeAppSlugs(d).length > 0}
            connected={apps}
            onPick={(a) =>
              patch({
                appSlugs: [...new Set([...nodeAppSlugs(d), a.slug])],
                // The label follows the FIRST app, so a step named after its app keeps that name
                // when a second one is added alongside it.
                ...(nodeAppSlugs(d).length === 0 ? { label: a.name } : {}),
              })
            }
          />
          {nodeAppSlugs(d).length > 0 ? (
            <ul className="flex flex-wrap gap-1">
              {nodeAppSlugs(d).map((slug) => (
                <li
                  key={slug}
                  className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-muted/30 px-1.5 py-0.5"
                >
                  <img
                    src={logoFor(slug)}
                    alt=""
                    className="size-3.5 shrink-0 rounded object-contain"
                    onError={(e) =>
                      ((e.currentTarget as HTMLImageElement).style.visibility = "hidden")
                    }
                  />
                  <span className="text-[11px] text-foreground/90">{prettyApp(slug)}</span>
                  <button
                    type="button"
                    aria-label={t("workflows.canvas.removeApp")}
                    onClick={() => patch({ appSlugs: nodeAppSlugs(d).filter((x) => x !== slug) })}
                    className="nodrag text-muted-foreground/40 hover:text-red-500"
                  >
                    <XIcon className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {nodeAppSlugs(d).length > 1 ? (
            <div className="nodrag flex items-center gap-1 rounded-md border border-border/60 p-0.5">
              {(["all", "any"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => patch({ appMode: mode })}
                  className={`flex-1 rounded px-1.5 py-0.5 text-[10px] font-semibold transition ${
                    (d.appMode ?? "all") === mode
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {mode === "all"
                    ? t("workflows.canvas.appModeAll")
                    : t("workflows.canvas.appModeAny")}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      }
    >
      <div className="flex flex-col gap-1.5">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
          {t("workflows.canvas.appDoInOrder")}
        </div>
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-1">
            <span className="text-[10px] tabular-nums text-muted-foreground/50">{i + 1}</span>
            <AutoTextarea
              value={s}
              onChange={(v) => setStep(i, v)}
              placeholder={t("workflows.canvas.appActionPlaceholder")}
              minPx={30}
              maxPx={180}
            />
            {steps.length > 1 ? (
              <button
                type="button"
                onClick={() => removeStep(i)}
                className="nodrag text-muted-foreground/40 hover:text-red-500"
                aria-label={t("workflows.canvas.removeAction")}
              >
                <Trash2Icon className="size-3" />
              </button>
            ) : null}
          </div>
        ))}
        <button
          type="button"
          onClick={addStep}
          className="nodrag mt-0.5 inline-flex w-fit items-center gap-1 rounded-md px-1 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/10"
        >
          <PlusIcon className="size-3" /> {t("workflows.canvas.thenMore")}
        </button>
      </div>
      <FlowChips id={id} kind="app" />
    </NodeShell>
  );
}

function BrowserNode({ id, data }: NodeProps) {
  const d = data as WfNodeData;
  const { t } = useI18n();
  const patch = usePatch(id);
  return (
    <NodeShell
      kind="browser"
      model={nodeModel("browser", t)}
      onDelete={useDelete(id)}
      hasIn
      hasOut
      title={null}
    >
      <AutoTextarea
        value={d.instruction ?? d.text ?? ""}
        onChange={(v) => patch({ text: v, instruction: v })}
        placeholder={t("workflows.canvas.browserPlaceholder")}
        minPx={56}
      />
      <FlowChips id={id} kind="browser" />
    </NodeShell>
  );
}

/** Cloud Browser — a managed, always-on browser (no device/extension needed). Prompt-driven
 * like the local browser; dedicated Browser Use tool routing is the engine's roadmap. */
function CloudBrowserNode({ id, data }: NodeProps) {
  const d = data as WfNodeData;
  const { t } = useI18n();
  const patch = usePatch(id);
  return (
    <NodeShell
      kind="cloudBrowser"
      model={nodeModel("cloudBrowser", t)}
      onDelete={useDelete(id)}
      hasIn
      hasOut
      title={null}
    >
      <AutoTextarea
        value={d.instruction ?? d.text ?? ""}
        onChange={(v) => patch({ text: v, instruction: v })}
        placeholder={t("workflows.canvas.cloudBrowserPlaceholder")}
        minPx={56}
      />
      <FlowChips id={id} kind="cloudBrowser" />
    </NodeShell>
  );
}

/** Cloud Computer — a compute box: run code, call an API, process files. Prompt-driven;
 * dedicated shell tool routing is the engine's roadmap. */
function ComputerNode({ id, data }: NodeProps) {
  const d = data as WfNodeData;
  const { t } = useI18n();
  const patch = usePatch(id);
  return (
    <NodeShell
      kind="computer"
      model={nodeModel("computer", t)}
      onDelete={useDelete(id)}
      hasIn
      hasOut
      title={null}
    >
      <AutoTextarea
        value={d.instruction ?? d.text ?? ""}
        onChange={(v) => patch({ text: v, instruction: v })}
        placeholder={t("workflows.canvas.computerPlaceholder")}
        minPx={56}
      />
      <FlowChips id={id} kind="computer" />
    </NodeShell>
  );
}

/** End node — what happens when the flow finishes. Rethought so it no longer ALWAYS messages:
 * pick a mode (message / save / webhook / just finish). Messaging is now explicit + opt-in per
 * mode, which fixes the "it keeps messaging" default. Loop-back is offered but clearly marked
 * "not run yet" — the engine runs each node once, so it captures intent without faking iteration. */
function OutputNode({ id, data }: NodeProps) {
  const d = data as WfNodeData;
  const { t } = useI18n();
  const patch = usePatch(id);
  const channels = useMemo(() => outputChannels(t), [t]);
  const mode = d.endMode ?? "message";
  const END_MODES: ReadonlyArray<{ value: NonNullable<WfNodeData["endMode"]>; label: string }> = [
    { value: "message", label: t("workflows.canvas.end.modeMessage") },
    { value: "save", label: t("workflows.canvas.end.modeSave") },
    { value: "webhook", label: t("workflows.canvas.end.modeWebhook") },
    { value: "none", label: t("workflows.canvas.end.modeNone") },
  ];
  return (
    <NodeShell
      kind="output"
      model={nodeModel("output", t)}
      onDelete={useDelete(id)}
      hasIn
      hasOut={false}
      title={null}
    >
      <AutoTextarea
        value={d.instruction ?? d.text ?? ""}
        onChange={(v) => patch({ text: v, instruction: v })}
        placeholder={t("workflows.canvas.outputPlaceholder")}
        minPx={56}
      />
      <div className="mt-2 flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
            {t("workflows.canvas.end.whenDone")}
          </span>
          <select
            value={mode}
            onChange={(e) =>
              patch({ endMode: e.target.value as NonNullable<WfNodeData["endMode"]> })
            }
            className={`${FIELD} py-1`}
          >
            {END_MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {mode === "message" ? (
          <>
            <div className="flex items-center gap-1.5">
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                {t("workflows.canvas.sendOn")}
              </span>
              <select
                value={d.channel ?? ""}
                onChange={(e) => patch({ channel: e.target.value })}
                className={`${FIELD} py-1`}
              >
                {channels.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <label className="nodrag flex cursor-pointer items-center gap-1.5 text-[11px] text-foreground/80">
              <input
                type="checkbox"
                checked={d.autonomous ?? false}
                onChange={(e) => patch({ autonomous: e.target.checked })}
              />
              {t("workflows.canvas.autonomous")}
            </label>
          </>
        ) : null}

        {mode === "webhook" ? (
          <input
            value={d.webhookUrl ?? ""}
            onChange={(e) => patch({ webhookUrl: e.target.value })}
            placeholder={t("workflows.canvas.end.webhookPlaceholder")}
            className={FIELD}
          />
        ) : null}
      </div>
      <FlowChips id={id} kind="output" />
    </NodeShell>
  );
}

/** Decision block — a focused sub-agent whose only job is to judge against a rule, always on the
 * cheap classifier model. Two shapes, and the card says which: with NO routes it is the relevance
 * gate, which runs FIRST and short-circuits before costly work; with routes it is a router, which
 * runs in its own place in the flow and picks which route (possibly a loop) continues. */
function DecisionNode({ id, data }: NodeProps) {
  const d = data as WfNodeData;
  const { t } = useI18n();
  const patch = usePatch(id);
  const routes = d.routes ?? [];
  const routing = hasRoutes(d.routes);
  const whenLabels = useConditionLabels();
  return (
    <NodeShell
      kind="decision"
      model={nodeModel("decision", t, routing)}
      onDelete={useDelete(id)}
      hasIn
      hasOut={!routing}
      {...(routing
        ? { outPorts: routes.map((r) => ({ id: r.id, label: r.label?.trim() || r.id })) }
        : {})}
      title={
        <div className="mb-1 text-[11px] text-muted-foreground">
          {routing ? t("workflows.canvas.routes.cardHint") : t("workflows.canvas.decisionHint")}
        </div>
      }
    >
      {routing ? (
        // Conditions are edited in the inspector, but the card STATES them — a routing step must
        // never be the silent black box a for-each step still is.
        <ol className="flex flex-col gap-1">
          {routes.map((r) => (
            <li
              key={r.id}
              className="flex flex-col rounded-md border border-border/60 bg-muted/20 px-2 py-1"
            >
              <span className="truncate text-[11px] font-medium text-foreground">
                {r.label?.trim() || r.id}
              </span>
              <span className="truncate text-[10px] text-muted-foreground">
                {conditionSummary(r.when, whenLabels)}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <AutoTextarea
          value={d.instruction ?? d.text ?? ""}
          onChange={(v) => patch({ text: v, instruction: v })}
          placeholder={t("workflows.canvas.decisionPlaceholder")}
          minPx={48}
        />
      )}
      <FlowChips id={id} kind="decision" />
    </NodeShell>
  );
}

/** The translated name of each route condition, shared by the card and the inspector. */
function useConditionLabels(): Readonly<Record<WfRouteConditionKind, string>> {
  const { t } = useI18n();
  return {
    hasMoreItems: t("workflows.canvas.routes.when.hasMoreItems"),
    contains: t("workflows.canvas.routes.when.contains"),
    notContains: t("workflows.canvas.routes.when.notContains"),
    ask: t("workflows.canvas.routes.when.ask"),
    always: t("workflows.canvas.routes.when.always"),
  };
}

/** Vault block — either tick specific files, or point at a folder to use its NEWEST image. */
function VaultNode({ id, data }: NodeProps) {
  const d = data as WfNodeData;
  const { t } = useI18n();
  const vault = useContext(VaultContext);
  const patch = usePatch(id);
  const sel = d.vaultIds ?? [];
  const folder = d.folder ?? "";
  const [folders, setFolders] = useState<ReadonlyArray<{ id: string; name: string }>>([]);
  useEffect(() => {
    let alive = true;
    void fetchVaultFolders().then((fs) => {
      if (alive) setFolders(fs.map((f) => ({ id: f.id, name: f.name })));
    });
    return () => {
      alive = false;
    };
  }, []);
  const folderName = folders.find((f) => f.id === folder)?.name ?? folder;
  const toggle = (fid: string) =>
    patch({ vaultIds: sel.includes(fid) ? sel.filter((x) => x !== fid) : [...sel, fid] });
  return (
    <NodeShell
      kind="vault"
      onDelete={useDelete(id)}
      hasIn
      hasOut
      title={
        <div className="mb-1 text-[11px] text-muted-foreground">
          {folder
            ? `Newest image from “${folderName}”`
            : sel.length
              ? sel.length > 1
                ? t("workflows.canvas.vaultFilesSelectedPlural", { count: String(sel.length) })
                : t("workflows.canvas.vaultFilesSelected", { count: String(sel.length) })
              : t("workflows.canvas.vaultPickFiles")}
        </div>
      }
    >
      <select
        className="nodrag mb-1 w-full rounded border border-border bg-background px-1 py-0.5 text-[11px] text-foreground"
        value={folder ? `folder:${folder}` : "files"}
        onChange={(e) => {
          const v = e.target.value;
          if (v.startsWith("folder:")) patch({ folder: v.slice("folder:".length), vaultIds: [] });
          else patch({ folder: "" });
        }}
      >
        <option value="files">Specific files (tick below)</option>
        {folders.length > 0 ? (
          <optgroup label="Newest image from folder">
            {folders.map((f) => (
              <option key={f.id} value={`folder:${f.id}`}>
                {f.name}
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
      {folder ? (
        <p className="text-[11px] text-muted-foreground/80">
          Posts the most recently added image in “{folderName}”.
        </p>
      ) : vault.length === 0 ? (
        <p className="text-[11px] text-muted-foreground/60">{t("workflows.canvas.vaultEmpty")}</p>
      ) : (
        <div className="nodrag flex max-h-36 flex-col gap-0.5 overflow-y-auto">
          {vault.map((f) => (
            <label key={f.id} className="flex cursor-pointer items-center gap-1.5 text-[11px]">
              <input type="checkbox" checked={sel.includes(f.id)} onChange={() => toggle(f.id)} />
              <span className="truncate text-foreground/90">{f.name}</span>
            </label>
          ))}
        </div>
      )}
      <FlowChips id={id} kind="vault" />
    </NodeShell>
  );
}

/** Small uppercase field caption used inside the For each card. */
function FieldCaption({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
      {children}
    </div>
  );
}

/**
 * FOR EACH — the whole "do this for every row" loop as one block (docs/architecture/
 * simple-workflows.md): the list, the one step to do per item ({item.Column} fills from the
 * row), how many run at once, and where each result is saved. The engine owns the list, the
 * per-item results and the count, so none of that needs steps of its own.
 */
/** For each container layout. The box is a RESIZABLE node (NodeResizer) with a big default size;
 * body steps drop in below the header and can be dragged around inside it. */
const FE_DEFAULT_W = 420;
const FE_DEFAULT_H = 380;
const FE_HEADER_H = 150;
const CHILD_H = 150;

/**
 * FOR EACH dispatcher: a v2 CONTAINER (default for new ones) renders the parallel-map box whose
 * body is child nodes; a v1 node (has forEachConfig, from before the redesign) keeps its
 * self-contained card so existing workflows are untouched.
 */
function ForEachNode(props: NodeProps) {
  const d = props.data as WfNodeData;
  return d.forEachConfig ? <ForEachV1Card {...props} /> : <ForEachContainerNode {...props} />;
}

function ForEachV1Card({ id, data }: NodeProps) {
  const d = data as WfNodeData;
  const { t } = useI18n();
  const patch = usePatch(id);
  const nodes = useStore((s) => s.nodes) as Node<WfNodeData>[];
  const c = d.forEachConfig ?? {};
  const set = (p: Partial<WfForEachConfig>) => patch({ forEachConfig: { ...c, ...p } });
  const source = c.source ?? { kind: "sheet" as const };
  const at = c.concurrency ?? FOREACH_DEFAULT_AT_ONCE;
  const others = nodes.filter((n) => n.id !== id && n.data.kind !== "trigger");
  const BODY_KINDS: ReadonlyArray<{ value: WfForEachBodyKind; label: string }> = [
    { value: "browser", label: t("workflows.canvas.forEach.bodyBrowser") },
    { value: "app", label: t("workflows.canvas.forEach.bodyApp") },
    { value: "ai", label: t("workflows.canvas.forEach.bodyAi") },
  ];
  const seg = (on: boolean) =>
    `nodrag flex-1 rounded-md py-1 text-[11px] font-semibold tabular-nums transition ${on ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`;
  return (
    <NodeShell
      kind="foreach"
      model={nodeModel("foreach", t)}
      onDelete={useDelete(id)}
      hasIn
      hasOut
      title={null}
    >
      <div className="flex flex-col gap-2.5">
        <div>
          <FieldCaption>{t("workflows.canvas.forEach.list")}</FieldCaption>
          <select
            value={source.kind}
            onChange={(e) =>
              set({
                source:
                  e.target.value === "step"
                    ? { kind: "step", stepId: others[0]?.id ?? "" }
                    : { kind: "sheet", spreadsheetId: "", sheet: "" },
              })
            }
            className={`${FIELD} py-1`}
          >
            <option value="sheet">{t("workflows.canvas.forEach.sourceSheet")}</option>
            <option value="step">{t("workflows.canvas.forEach.sourceStep")}</option>
          </select>
          {source.kind === "sheet" ? (
            <div className="mt-1.5 flex flex-col gap-1.5">
              <input
                value={source.spreadsheetId ?? ""}
                onChange={(e) => set({ source: { ...source, spreadsheetId: e.target.value } })}
                placeholder={t("workflows.canvas.forEach.sheetLink")}
                className={FIELD}
              />
              <input
                value={source.sheet ?? ""}
                onChange={(e) => set({ source: { ...source, sheet: e.target.value } })}
                placeholder={t("workflows.canvas.forEach.sheetTab")}
                className={FIELD}
              />
            </div>
          ) : (
            <select
              value={source.stepId ?? ""}
              onChange={(e) => set({ source: { kind: "step", stepId: e.target.value } })}
              className={`${FIELD} mt-1.5 py-1`}
            >
              {others.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.data.label?.trim() || canvasKindLabel(n.data.kind, t)}
                </option>
              ))}
            </select>
          )}
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="shrink-0">{t("workflows.canvas.forEach.skipWhere")}</span>
            <input
              value={c.filter?.column ?? ""}
              onChange={(e) => set({ filter: { ...c.filter, column: e.target.value } })}
              placeholder={t("workflows.canvas.forEach.column")}
              className={`${FIELD} py-1`}
            />
            <span className="shrink-0">=</span>
            <input
              value={c.filter?.notEquals ?? ""}
              onChange={(e) => set({ filter: { ...c.filter, notEquals: e.target.value } })}
              placeholder={t("workflows.canvas.forEach.value")}
              className={`${FIELD} py-1`}
            />
          </div>
        </div>

        <div>
          <FieldCaption>{t("workflows.canvas.forEach.eachItem")}</FieldCaption>
          <select
            value={c.body?.kind ?? "browser"}
            onChange={(e) =>
              set({ body: { ...c.body, kind: e.target.value as WfForEachBodyKind } })
            }
            className={`${FIELD} mb-1.5 py-1`}
          >
            {BODY_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          <AutoTextarea
            value={c.body?.text ?? ""}
            onChange={(v) => set({ body: { ...c.body, text: v } })}
            placeholder={t("workflows.canvas.forEach.bodyPlaceholder")}
            minPx={56}
          />
        </div>

        <div>
          <FieldCaption>{t("workflows.canvas.forEach.atOnce")}</FieldCaption>
          <div className="flex gap-0.5 rounded-lg border border-border bg-background p-0.5">
            {[1, 2, 3, 4, 5].map((k) => (
              <button
                key={k}
                type="button"
                className={seg(at === k)}
                onClick={() => set({ concurrency: k })}
              >
                {k}
              </button>
            ))}
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <input
              type="number"
              min={0}
              max={600}
              value={c.staggerSeconds ?? FOREACH_DEFAULT_STAGGER}
              onChange={(e) =>
                set({ staggerSeconds: Math.max(0, Math.min(600, Number(e.target.value) || 0)) })
              }
              className={`${FIELD} w-14 py-1 tabular-nums`}
            />
            <span className="shrink-0">{t("workflows.canvas.forEach.secondsApart")}</span>
            <select
              value={c.onFail ?? "skip"}
              onChange={(e) => set({ onFail: e.target.value === "stop" ? "stop" : "skip" })}
              className={`${FIELD} py-1`}
            >
              <option value="skip">{t("workflows.canvas.forEach.onFailSkip")}</option>
              <option value="stop">{t("workflows.canvas.forEach.onFailStop")}</option>
            </select>
          </div>
        </div>

        {source.kind === "sheet" ? (
          <div>
            <FieldCaption>{t("workflows.canvas.forEach.saveTo")}</FieldCaption>
            <div className="flex gap-1.5">
              <input
                value={c.saveTo?.outcomeColumn ?? ""}
                onChange={(e) => set({ saveTo: { ...c.saveTo, outcomeColumn: e.target.value } })}
                placeholder={t("workflows.canvas.forEach.outcomeColumn")}
                className={FIELD}
              />
              <input
                value={c.saveTo?.noteColumn ?? ""}
                onChange={(e) => set({ saveTo: { ...c.saveTo, noteColumn: e.target.value } })}
                placeholder={t("workflows.canvas.forEach.noteColumn")}
                className={FIELD}
              />
            </div>
          </div>
        ) : null}

        <label className="nodrag flex cursor-pointer items-start gap-1.5 text-[11px] text-foreground/80">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={c.dryRun === true}
            onChange={(e) => set({ dryRun: e.target.checked })}
          />
          <span>
            {t("workflows.canvas.forEach.dryRun")}
            <span className="block text-[10px] text-muted-foreground">
              {t("workflows.canvas.forEach.dryRunHint")}
            </span>
          </span>
        </label>
      </div>
    </NodeShell>
  );
}

/**
 * FOR EACH v2 — the CONTAINER on the canvas. A box you drop per-item steps INTO (child nodes with
 * parentId). The list comes in on the left (an edge from the step before), the collected results
 * leave on the right (an edge to the step after). The header carries "N at a time" + the fail /
 * dry-run controls. Children are ordinary step cards rendered inside by React Flow.
 */
function ForEachContainerNode({ id, data, selected }: NodeProps) {
  const d = data as WfNodeData;
  const { t } = useI18n();
  const patch = usePatch(id);
  const del = useDelete(id);
  const { setNodes, setEdges } = useReactFlow();
  const children = useStore((s) =>
    (s.nodes as Node<WfNodeData>[]).filter((n) => n.parentId === id),
  );
  const at = d.concurrency ?? 3;
  const seg = (on: boolean) =>
    `nodrag flex-1 rounded-md py-0.5 text-[11px] font-semibold tabular-nums transition ${on ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`;

  // A new body step drops inside the box, stacked under the last one, and chains from it so the
  // per-item flow reads top-to-bottom. The user can then drag it anywhere inside the container.
  const addInside = (kind: WfNodeKind) => {
    const childId = nid();
    const count = children.length;
    const last = children[children.length - 1];
    const child: Node<WfNodeData> = {
      id: childId,
      type: rfType(kind),
      parentId: id,
      extent: "parent",
      position: { x: 24, y: FE_HEADER_H + 14 + count * (CHILD_H + 18) },
      data: defaultNodeData(kind, t),
    };
    setNodes((ns) => [...ns, child]);
    if (last)
      setEdges((es) => [
        ...es,
        { id: `e_${last.id}_${childId}`, source: last.id, target: childId, type: "insert" },
      ]);
  };

  const a = ACCENT.foreach;
  return (
    <div
      className="relative h-full w-full rounded-2xl border-2 border-dashed border-orange-500/50 bg-orange-500/[0.05]"
      style={{ minWidth: FE_DEFAULT_W, minHeight: FE_DEFAULT_H }}
    >
      {/* Drag any edge/corner to resize the box so more steps fit. */}
      <NodeResizer
        minWidth={320}
        minHeight={200}
        isVisible={selected === true}
        lineClassName="!border-orange-500/50"
        handleClassName="!size-2.5 !rounded-sm !border-orange-500 !bg-background"
      />
      <Handle
        type="target"
        position={Position.Left}
        className="!size-2.5 !border-2 !border-background !bg-muted-foreground/50"
      />
      {/* Header — always on top, never covered: label, the per-item controls, delete. */}
      <div className="rounded-t-2xl bg-orange-500/[0.06] px-3 pb-2 pt-2.5">
        <div className="flex items-center gap-2">
          <span className={`flex size-6 items-center justify-center rounded-lg ${a.tint}`}>
            {a.icon}
          </span>
          <span
            className={`rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${a.chip}`}
          >
            {canvasKindLabel("foreach", t)}
          </span>
          <button
            type="button"
            onClick={del}
            className="ml-auto text-muted-foreground/40 transition hover:text-red-500"
            aria-label={t("workflows.canvas.delete")}
          >
            <Trash2Icon className="size-3.5" />
          </button>
        </div>
        <div className="mt-1.5 flex items-center gap-1.5">
          <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground/70">
            {t("workflows.canvas.forEach.atOnce")}
          </span>
          <div className="flex flex-1 gap-0.5 rounded-lg border border-border bg-background p-0.5">
            {[1, 2, 3, 5, 8, 10].map((k) => (
              <button
                key={k}
                type="button"
                className={seg(at === k)}
                onClick={() => patch({ concurrency: k })}
              >
                {k}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <input
            type="number"
            min={0}
            max={600}
            value={d.staggerSeconds ?? 20}
            onChange={(e) =>
              patch({ staggerSeconds: Math.max(0, Math.min(600, Number(e.target.value) || 0)) })
            }
            className={`${FIELD} nodrag w-14 py-0.5 tabular-nums`}
          />
          <span className="shrink-0">{t("workflows.canvas.forEach.secondsApart")}</span>
          <select
            value={d.onFail ?? "skip"}
            onChange={(e) => patch({ onFail: e.target.value === "stop" ? "stop" : "skip" })}
            className={`${FIELD} nodrag py-0.5`}
          >
            <option value="skip">{t("workflows.canvas.forEach.onFailSkip")}</option>
            <option value="stop">{t("workflows.canvas.forEach.onFailStop")}</option>
          </select>
          <label className="nodrag flex shrink-0 cursor-pointer items-center gap-1 text-[10px] text-foreground/80">
            <input
              type="checkbox"
              checked={d.dryRun === true}
              onChange={(e) => patch({ dryRun: e.target.checked })}
            />
            {t("workflows.canvas.forEach.dryRun")}
          </label>
        </div>
      </div>
      <div className="px-3 pt-1.5 text-[9px] font-semibold uppercase tracking-wide text-orange-600/70 dark:text-orange-400/70">
        {t("workflows.canvas.forEach.perItem")}
      </div>
      {/* The body steps render here as draggable child nodes (React Flow, by parentId). */}
      {children.length === 0 ? (
        <div className="mx-3 mt-1 rounded-xl border border-dashed border-border bg-background/40 px-3 py-5 text-center">
          <div className="text-[11px] font-medium text-foreground">
            {t("workflows.canvas.forEach.bodyEmptyTitle")}
          </div>
          <div className="mb-2 text-[10px] text-muted-foreground">
            {t("workflows.canvas.forEach.bodyEmptyHint")}
          </div>
          <div className="flex justify-center gap-1.5">
            <button
              type="button"
              onClick={() => addInside("browser")}
              className={BTN_SOFT + " !w-auto"}
            >
              <SmartphoneIcon className="size-3.5" /> {canvasKindLabel("browser", t)}
            </button>
            <button
              type="button"
              onClick={() => addInside("app")}
              className={BTN_SOFT + " !w-auto"}
            >
              <LayoutGridIcon className="size-3.5" /> {canvasKindLabel("app", t)}
            </button>
          </div>
        </div>
      ) : (
        <div className="absolute bottom-2 right-3 z-10 flex gap-1">
          <button
            type="button"
            onClick={() => addInside("browser")}
            className="nodrag inline-flex items-center gap-1 rounded-lg border border-dashed border-border bg-background/80 px-2 py-1 text-[10px] font-semibold text-muted-foreground transition hover:text-foreground"
          >
            <PlusIcon className="size-3" /> {t("workflows.canvas.forEach.addStep")}
          </button>
        </div>
      )}
      <Handle
        type="source"
        position={Position.Right}
        className="!size-2.5 !border-2 !border-background !bg-primary"
      />
    </div>
  );
}

// NOTE: React Flow reserves the type names "input"/"output"/"default"/"group" and
// styles them with a default card. We prefix ours ("wf*") so our custom cards render
// alone — otherwise the built-in "output" card draws a second box behind ours.
const NODE_TYPES = {
  wfTrigger: TriggerNode,
  wfApp: AppNode,
  wfBrowser: BrowserNode,
  wfCloudBrowser: CloudBrowserNode,
  wfComputer: ComputerNode,
  wfVault: VaultNode,
  wfDecision: DecisionNode,
  wfForEach: ForEachNode,
  wfOutput: OutputNode,
};
const RF_TYPE: Record<WfNodeKind, string> = {
  trigger: "wfTrigger",
  app: "wfApp",
  browser: "wfBrowser",
  cloudBrowser: "wfCloudBrowser",
  computer: "wfComputer",
  vault: "wfVault",
  decision: "wfDecision",
  foreach: "wfForEach",
  output: "wfOutput",
};
const rfType = (kind: WfNodeKind): string => RF_TYPE[kind] ?? "wfApp";

/** Built-in "apps" that are really the file Vault, not a Composio integration. An AI
 * build (or an old graph) may have picked one as an App step — coerce it to a real Vault
 * node so the user gets the file checkboxes instead of a dead app card. */
const VAULT_SLUGS = new Set(["vault", "files", "file", "documents", "docs"]);
function normalizeNodeData(data: WfNodeData): WfNodeData {
  if (data.kind === "app" && data.appSlug && VAULT_SLUGS.has(data.appSlug.toLowerCase())) {
    return {
      kind: "vault",
      label: "Vault",
      vaultIds: data.vaultIds ?? [],
      ...(data.folder ? { folder: data.folder } : {}),
    };
  }
  return data;
}

let nodeSeq = 0;
const nid = () => `n_${Date.now().toString(36)}_${(nodeSeq += 1)}`;

/**
 * Per-node inspector — the right-docked panel that opens when a step is selected. It's where
 * the per-node model, this-step instruction, and DATA DEPENDENCIES ("Uses:") are edited.
 *
 * Dependencies stay EDGE-BACKED (the engine reads dependsOn only from edges): adding a source
 * upserts an edge source→thisNode with data.dependsOn:true; removing it drops a data-only edge
 * or clears the flag on a control edge. Instruction mirrors to data.text so the compiled-blob
 * fallback stays in sync; model writes data.model (absent = kind default).
 */
/** Editor for a node's NAMED OUTPUT fields (Fix C). Chips with a remove button + an add input;
 * a downstream step references these as {{ThisStep.field}}. Dedupes, trims, ignores blanks. */
function OutputFieldsEditor({
  outputs,
  patch,
}: {
  outputs: string[];
  patch: (p: Partial<WfNodeData>) => void;
}) {
  const { t } = useI18n();
  const [draft, setDraft] = useState("");
  const add = () => {
    const name = draft.trim();
    if (!name) return;
    if (!outputs.some((o) => o.toLowerCase() === name.toLowerCase()))
      patch({ outputs: [...outputs, name] });
    setDraft("");
  };
  const remove = (name: string) => patch({ outputs: outputs.filter((o) => o !== name) });
  return (
    <div>
      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {t("workflows.canvas.insp.outputs")}
      </div>
      {outputs.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {outputs.map((name) => (
            <span
              key={name}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-1.5 py-0.5 text-[11px] font-medium text-foreground"
            >
              {name}
              <button
                type="button"
                onClick={() => remove(name)}
                aria-label={t("workflows.canvas.insp.removeOutput")}
                className="text-muted-foreground/50 hover:text-red-500"
              >
                <XIcon className="size-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
        placeholder={t("workflows.canvas.insp.outputsPlaceholder")}
        className={FIELD}
      />
      <p className="mt-1.5 text-[10.5px] leading-snug text-muted-foreground">
        {t("workflows.canvas.insp.outputsHint")}
      </p>
    </div>
  );
}

/** The For each CONTAINER's settings, shown in the right-hand inspector so they are always
 * reachable — a body step on the canvas can never cover them. */
function ForEachContainerSettings({
  d,
  patch,
}: {
  d: WfNodeData;
  patch: (p: Partial<WfNodeData>) => void;
}) {
  const { t } = useI18n();
  const at = d.concurrency ?? 3;
  const seg = (on: boolean) =>
    `nodrag flex-1 rounded-md py-1 text-[11px] font-semibold tabular-nums transition ${on ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`;
  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-border bg-muted/20 p-3">
      <div>
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {t("workflows.canvas.forEach.atOnce")}
        </div>
        <div className="flex gap-0.5 rounded-lg border border-border bg-background p-0.5">
          {[1, 2, 3, 5, 8, 10].map((k) => (
            <button
              key={k}
              type="button"
              className={seg(at === k)}
              onClick={() => patch({ concurrency: k })}
            >
              {k}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <input
          type="number"
          min={0}
          max={600}
          value={d.staggerSeconds ?? 20}
          onChange={(e) =>
            patch({ staggerSeconds: Math.max(0, Math.min(600, Number(e.target.value) || 0)) })
          }
          className={`${FIELD} w-16 py-1 tabular-nums`}
        />
        <span className="shrink-0">{t("workflows.canvas.forEach.secondsApart")}</span>
        <select
          value={d.onFail ?? "skip"}
          onChange={(e) => patch({ onFail: e.target.value === "stop" ? "stop" : "skip" })}
          className={`${FIELD} py-1`}
        >
          <option value="skip">{t("workflows.canvas.forEach.onFailSkip")}</option>
          <option value="stop">{t("workflows.canvas.forEach.onFailStop")}</option>
        </select>
      </div>
      <label className="nodrag flex cursor-pointer items-start gap-1.5 text-[11px] text-foreground/80">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={d.dryRun === true}
          onChange={(e) => patch({ dryRun: e.target.checked })}
        />
        <span>
          {t("workflows.canvas.forEach.dryRun")}
          <span className="block text-[10px] text-muted-foreground">
            {t("workflows.canvas.forEach.dryRunHint")}
          </span>
        </span>
      </label>
    </div>
  );
}

function NodeInspector({ nodeId, onClose }: { nodeId: string; onClose: () => void }) {
  const { t } = useI18n();
  const { setNodes, setEdges } = useReactFlow();
  const nodes = useStore((s) => s.nodes);
  const edges = useStore((s) => s.edges);
  const node = nodes.find((n) => n.id === nodeId) as Node<WfNodeData> | undefined;
  useEffect(() => {
    if (!node) onClose();
  }, [node, onClose]);
  if (!node) return null;
  const d = node.data;
  const kind = d.kind;
  const a = ACCENT[kind];
  const patch = (p: Partial<WfNodeData>) =>
    setNodes((ns) => ns.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...p } } : n)));
  // Remove the per-node model override entirely (back to the kind default). Can't `patch({model:
  // undefined})` under exactOptionalPropertyTypes — the key must be absent, not set to undefined.
  const clearModel = () =>
    setNodes((ns) =>
      ns.map((n) => {
        if (n.id !== nodeId) return n;
        const { model: _omit, ...rest } = n.data as WfNodeData;
        return { ...n, data: rest };
      }),
    );

  const incoming = edges.filter((e) => e.target === nodeId);
  const depEdges = incoming.filter(
    (e) => (e.data as { dependsOn?: boolean } | undefined)?.dependsOn,
  );
  const usesIds = new Set(depEdges.map((e) => e.source));
  const nodeLabel = (n: Node<WfNodeData> | undefined): string =>
    n ? n.data.label?.trim() || canvasKindLabel(n.data.kind, t) : "";
  const candidates = nodes.filter(
    (n) =>
      n.id !== nodeId &&
      !usesIds.has(n.id) &&
      (
        ["app", "browser", "cloudBrowser", "computer", "vault", "decision"] as WfNodeKind[]
      ).includes((n.data as WfNodeData).kind),
  ) as Node<WfNodeData>[];

  const addSource = (srcId: string) => {
    if (!srcId) return;
    setEdges((es) => {
      const existing = es.find((e) => e.source === srcId && e.target === nodeId);
      if (existing)
        return es.map((e) =>
          e.id === existing.id ? { ...e, data: { ...e.data, dependsOn: true } } : e,
        );
      return [
        ...es,
        {
          id: `dep_${srcId}_${nodeId}`,
          source: srcId,
          target: nodeId,
          type: "insert",
          data: { dependsOn: true, dataOnly: true },
        } as Edge,
      ];
    });
  };
  const removeSource = (srcId: string) =>
    setEdges((es) =>
      es.flatMap((e) => {
        if (!(e.source === srcId && e.target === nodeId)) return [e];
        const ed = e.data as { dependsOn?: boolean; dataOnly?: boolean } | undefined;
        if (!ed?.dependsOn) return [e];
        // A data-only edge we created is removed entirely; a control edge just loses the flag.
        return ed.dataOnly ? [] : [{ ...e, data: { ...e.data, dependsOn: false } }];
      }),
    );

  const isTask =
    kind === "app" ||
    kind === "browser" ||
    kind === "cloudBrowser" ||
    kind === "computer" ||
    kind === "output";
  const fast = d.model === GATE_MODEL;
  const showInstruction =
    kind === "app" || kind === "browser" || kind === "decision" || kind === "output";
  const instrLabel =
    kind === "app" ? t("workflows.canvas.insp.notes") : t("workflows.canvas.insp.instruction");
  const seg = (on: boolean) =>
    `flex-1 rounded-md px-2 py-1.5 text-[11px] font-semibold transition ${on ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`;

  return (
    <aside className="pointer-events-auto absolute inset-y-3 right-3 z-10 flex w-72 flex-col overflow-hidden rounded-2xl border border-border/70 bg-background/95 shadow-lg backdrop-blur">
      <div className="flex items-center gap-2 border-b border-border px-3.5 py-3">
        <span className={`flex size-6 items-center justify-center rounded-lg ${a.tint}`}>
          {a.icon}
        </span>
        <div className="min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
            {canvasKindLabel(kind, t)}
          </div>
          <div className="truncate text-[13px] font-semibold text-foreground">
            {d.label?.trim() || canvasKindLabel(kind, t)}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("workflows.canvas.insp.close")}
          className="ml-auto shrink-0 rounded-md p-1 text-muted-foreground/60 hover:bg-muted hover:text-foreground"
        >
          <XIcon className="size-4" />
        </button>
      </div>

      <div className="flex flex-col gap-4 overflow-y-auto p-3.5">
        {kind === "foreach" && !d.forEachConfig ? (
          <ForEachContainerSettings d={d} patch={patch} />
        ) : null}
        {showInstruction ? (
          <div>
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              {instrLabel}
            </div>
            <AutoTextarea
              value={d.instruction ?? d.text ?? ""}
              onChange={(v) => patch({ text: v, instruction: v })}
              placeholder={t("workflows.canvas.insp.instructionPlaceholder")}
              minPx={72}
              maxPx={420}
            />
          </div>
        ) : null}

        {isTask ? (
          <div>
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              {t("workflows.canvas.insp.model")}
            </div>
            <div className="flex gap-1 rounded-lg border border-border bg-card p-0.5">
              <button type="button" onClick={() => clearModel()} className={seg(!fast)}>
                {t("workflows.canvas.modelFull")}
              </button>
              <button
                type="button"
                onClick={() => patch({ model: GATE_MODEL })}
                className={seg(fast)}
              >
                {t("workflows.canvas.insp.modelFast")}
              </button>
            </div>
            <p className="mt-1.5 text-[10.5px] leading-snug text-muted-foreground">
              {t("workflows.canvas.insp.modelHint")}
            </p>
          </div>
        ) : kind === "decision" ? (
          <div>
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              {t("workflows.canvas.insp.model")}
            </div>
            <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
              {t(
                hasRoutes(d.routes)
                  ? "workflows.canvas.insp.routerModelNote"
                  : "workflows.canvas.insp.decisionModelNote",
              )}
            </div>
          </div>
        ) : null}

        {kind === "decision" ? <RoutesEditor nodeId={nodeId} data={d} patch={patch} /> : null}

        {isTask ? (
          <div className="flex flex-col gap-2">
            <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-border/60 bg-card px-3 py-2">
              <input
                type="checkbox"
                checked={d.forEach === true}
                onChange={(e) => patch({ forEach: e.target.checked })}
                className="mt-0.5"
              />
              <span className="text-[11px] leading-snug text-foreground">
                Run once per item of the incoming list
                <span className="mt-0.5 block text-[10px] text-muted-foreground">
                  Loops this step over each item the step it depends on produced.
                </span>
              </span>
            </label>
            {/* When this step loops AND has 2+ data sources, pick WHICH source is the list. */}
            {d.forEach === true && depEdges.length >= 1 ? (
              <div>
                <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  {t("workflows.canvas.insp.forEachSource")}
                </div>
                <select
                  value={d.forEachSource ?? depEdges[0]?.source ?? ""}
                  onChange={(e) => patch({ forEachSource: e.target.value })}
                  className={`${FIELD} cursor-pointer`}
                >
                  {depEdges.map((e) => (
                    <option key={e.id} value={e.source}>
                      {nodeLabel(
                        nodes.find((n) => n.id === e.source) as Node<WfNodeData> | undefined,
                      )}
                    </option>
                  ))}
                </select>
                <p className="mt-1.5 text-[10.5px] leading-snug text-muted-foreground">
                  {t("workflows.canvas.insp.forEachSourceHint")}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        {isTask ? <OutputFieldsEditor outputs={d.outputs ?? []} patch={patch} /> : null}

        {kind !== "trigger" ? (
          <div>
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              {t("workflows.canvas.insp.uses")}
            </div>
            {depEdges.length > 0 ? (
              <div className="mb-2 flex flex-col gap-1.5">
                {depEdges.map((e) => (
                  <div
                    key={e.id}
                    className="flex items-center gap-1.5 rounded-lg border border-teal-500/25 bg-teal-500/10 px-2 py-1.5"
                  >
                    <DatabaseIcon className="size-3 shrink-0 text-teal-600 dark:text-teal-400" />
                    <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-foreground">
                      {nodeLabel(
                        nodes.find((n) => n.id === e.source) as Node<WfNodeData> | undefined,
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSource(e.source)}
                      aria-label={t("workflows.canvas.insp.removeSource")}
                      className="shrink-0 text-muted-foreground/50 hover:text-red-500"
                    >
                      <XIcon className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mb-2 text-[10.5px] leading-snug text-muted-foreground">
                {t("workflows.canvas.insp.noSources")}
              </p>
            )}
            {candidates.length > 0 ? (
              <select
                value=""
                onChange={(e) => addSource(e.target.value)}
                className={`${FIELD} cursor-pointer`}
              >
                <option value="">{t("workflows.canvas.insp.addSource")}</option>
                {candidates.map((n) => (
                  <option key={n.id} value={n.id}>
                    {nodeLabel(n)}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        ) : null}

        <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-[10.5px] leading-snug text-muted-foreground">
          {depEdges.length > 0
            ? t("workflows.canvas.insp.waitsHint")
            : t("workflows.canvas.insp.parallelHint")}
        </div>
      </div>
    </aside>
  );
}

function CanvasInner({
  initial,
  seedTriggerLabels,
  onChange,
}: {
  initial: WfGraph | null;
  seedTriggerLabels: string[];
  onChange: (g: WfGraph) => void;
}) {
  const { t } = useI18n();
  const editTriggers = useContext(EditTriggersContext);
  const seeded = useMemo<WfGraph>(() => {
    if (initial && Array.isArray(initial.nodes) && initial.nodes.length > 0) {
      // Normalize node types to our prefixed ones (handles graphs saved before the rename)
      // and coerce any vault-as-app step into a real Vault node (file checkboxes).
      return {
        nodes: initial.nodes.map((n) => {
          const data = normalizeNodeData(n.data as WfNodeData);
          return { ...n, data, type: rfType(data.kind) };
        }),
        // Keep a saved edge's own type (a loop edge draws differently); only fill in the default
        // for edges saved before edge types existed. Spreading `e` keeps `sourceHandle`, which is
        // what remembers the route.
        edges: initial.edges.map((e) => ({ ...e, type: e.type ?? "insert" })),
      };
    }
    const triggers: Node<WfNodeData>[] =
      seedTriggerLabels.length > 0
        ? seedTriggerLabels.map((l, i) => ({
            id: nid(),
            type: rfType("trigger"),
            position: { x: 0, y: i * 130 },
            data: { label: l, kind: "trigger" as const },
          }))
        : [
            {
              id: nid(),
              type: rfType("trigger"),
              position: { x: 0, y: 60 },
              data: { label: t("workflows.canvas.defaultTrigger"), kind: "trigger" as const },
            },
          ];
    const app: Node<WfNodeData> = {
      id: nid(),
      type: rfType("app"),
      position: { x: 320, y: 40 },
      data: defaultNodeData("app", t),
    };
    const out: Node<WfNodeData> = {
      id: nid(),
      type: rfType("output"),
      position: { x: 680, y: 60 },
      data: defaultNodeData("output", t),
    };
    const edges: Edge[] = [
      ...triggers.map((t) => ({
        id: `e_${t.id}_${app.id}`,
        source: t.id,
        target: app.id,
        type: "insert",
      })),
      {
        id: `e_${app.id}_${out.id}`,
        source: app.id,
        target: out.id,
        type: "insert",
      },
    ];
    return { nodes: [...triggers, app, out], edges };
  }, [initial, seedTriggerLabels, t]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<WfNodeData>>(seeded.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(seeded.edges);
  // Which node's inspector is open. Selecting a node docks the per-node panel on the right;
  // clicking empty canvas closes it. Triggers are edited via the Triggers panel, not here.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const emit = useCallback(
    (ns: Node<WfNodeData>[], es: Edge[]) => onChange({ nodes: ns, edges: es }),
    [onChange],
  );

  // Propagate EVERY change up to the parent so Save always persists the latest graph.
  // In-node field edits (decision/output text, app steps, vault file ticks) update the
  // ReactFlow store via `patch` but DON'T go through the structural handlers that call
  // emit() — so without this, editing a node then saving silently kept the OLD graph
  // (the "changes don't stick" bug). Runs on mount too (emits the seeded graph, a
  // no-op) and on every node/edge change thereafter.
  useEffect(() => {
    emit(nodes, edges);
  }, [nodes, edges, emit]);
  const onConnect = useCallback(
    (c: Connection) =>
      setEdges((es) =>
        addEdge(
          // Dropped on a node's bottom "dep" handle → it's a DATA dependency (edge-backed
          // dependsOn), which the engine feeds into the target before it runs. Anything else is
          // normal control flow.
          c.targetHandle === "dep"
            ? { ...c, type: "insert", data: { dependsOn: true, dataOnly: true } }
            : { ...c, type: "insert" },
          es,
        ),
      ),
    [setEdges],
  );

  // Insert a step in the middle of an edge and auto-rewire source → new → target.
  const insertBetween = useCallback(
    (edgeId: string, sourceId: string, targetId: string, kind: WfNodeKind) => {
      // The edge being split may leave a named ROUTE port. The first new edge has to keep that
      // port, otherwise inserting a step into an arm silently detaches it from its route.
      const splitHandle = edges.find((e) => e.id === edgeId)?.sourceHandle ?? null;
      const s = nodes.find((n) => n.id === sourceId);
      // NB: name this `tgt`, NOT `t` — a local `t` shadowed the i18n translator, so
      // defaultNodeData(kind, t) below received the TARGET NODE instead of the translator
      // and produced broken node defaults (part of why an inserted step looked wrong).
      const tgt = nodes.find((n) => n.id === targetId);
      // Offset the inserted node BELOW the connecting line. At the exact midpoint of two
      // adjacent nodes it lands on top of / behind them (they're ~320px apart, nodes are
      // wider than that), so the step inserted but was invisible — "clicking + did nothing".
      const pos =
        s && tgt
          ? { x: (s.position.x + tgt.position.x) / 2, y: (s.position.y + tgt.position.y) / 2 + 160 }
          : { x: 320, y: 260 };
      const node: Node<WfNodeData> = {
        id: nid(),
        type: rfType(kind),
        position: pos,
        data: defaultNodeData(kind, t),
      };
      setNodes((ns) => {
        const next = [...ns, node];
        setEdges((es) => {
          const e2 = [
            ...es.filter((e) => e.id !== edgeId),
            {
              id: `e_${sourceId}_${node.id}`,
              source: sourceId,
              target: node.id,
              type: "insert",
              ...(splitHandle ? { sourceHandle: splitHandle } : {}),
            },
            {
              id: `e_${node.id}_${targetId}`,
              source: node.id,
              target: targetId,
              type: "insert",
            },
          ];
          emit(next, e2);
          return e2;
        });
        return next;
      });
    },
    [nodes, setNodes, setEdges, emit, t],
  );

  // Expose the current insert fn to the custom edges via the module handle (context to
  // React Flow edges was unreliable — see activeInsert). Reads at click time = always fresh.
  useEffect(() => {
    activeInsert = insertBetween;
    return () => {
      activeInsert = () => {};
    };
  }, [insertBetween]);

  const addNode = useCallback(
    (kind: WfNodeKind) => {
      const output = nodes.find((n) => (n.data as WfNodeData).kind === "output") ?? null;
      const chainable = nodes.filter((n) => (n.data as WfNodeData).kind !== "output");
      const tail =
        chainable.length > 0
          ? chainable.reduce((a, b) => (a.position.x >= b.position.x ? a : b))
          : null;

      const isStep =
        kind === "app" ||
        kind === "browser" ||
        kind === "cloudBrowser" ||
        kind === "computer" ||
        kind === "vault" ||
        kind === "decision" ||
        kind === "foreach";

      // Selection-aware insertion: a new STEP attaches to the node the user has SELECTED
      // (falling back to the tail), splicing into that node's outgoing edge so it lands
      // exactly where they're working — instead of a detached card at the far right.
      if (isStep) {
        const selected =
          selectedId != null
            ? (nodes.find((n) => n.id === selectedId) as Node<WfNodeData> | undefined)
            : undefined;
        const anchor = selected && selected.data.kind !== "output" ? selected : tail;
        if (anchor) {
          // A routing node has one edge per arm, so "its outgoing edge" is ambiguous — splicing
          // into whichever came first would hijack an arbitrary route. Append after it instead
          // and let the author drag the arm they meant.
          const anchorRoutes = hasRoutes((anchor.data as WfNodeData).routes);
          const outEdge = anchorRoutes ? undefined : edges.find((e) => e.source === anchor.id);
          if (outEdge) {
            // Splice between the anchor and whatever it currently flows into.
            insertBetween(outEdge.id, anchor.id, outEdge.target, kind);
            return;
          }
          // Anchor has no downstream yet — append directly after it.
          const node: Node<WfNodeData> = {
            id: nid(),
            type: rfType(kind),
            position: { x: anchor.position.x + 320, y: anchor.position.y },
            data: defaultNodeData(kind, t),
          };
          setNodes((ns) => {
            const next = [...ns, node];
            setEdges((es) => {
              const e2 = [
                ...es,
                {
                  id: `e_${anchor.id}_${node.id}`,
                  source: anchor.id,
                  target: node.id,
                  type: "insert",
                },
              ];
              emit(next, e2);
              return e2;
            });
            return next;
          });
          return;
        }
      }

      // Non-step (trigger / output) or an empty canvas: drop a standalone node, no auto-chain.
      const x = tail ? tail.position.x + 320 : 320;
      const y = tail ? tail.position.y : 60;
      const node: Node<WfNodeData> = {
        id: nid(),
        type: rfType(kind),
        position: {
          x: kind === "output" ? x : output ? Math.min(x, output.position.x - 320) : x,
          y: kind === "trigger" ? y + 150 : y,
        },
        data: defaultNodeData(kind, t),
        ...(kind === "foreach" ? { style: { width: FE_DEFAULT_W, height: FE_DEFAULT_H } } : {}),
      };
      setNodes((ns) => {
        const next = [...ns, node];
        emit(next, edges);
        return next;
      });
    },
    [nodes, edges, selectedId, insertBetween, setNodes, setEdges, emit, t],
  );

  const PALETTE: ReadonlyArray<[WfNodeKind, ReactNode]> = [
    ["trigger", <ZapIcon key="z" className="size-4" />],
    ["foreach", <RepeatIcon key="fe" className="size-4" />],
    ["app", <LayoutGridIcon key="g" className="size-4" />],
    ["browser", <SmartphoneIcon key="b" className="size-4" />],
    ["cloudBrowser", <CloudIcon key="cb" className="size-4" />],
    ["computer", <CpuIcon key="cp" className="size-4" />],
    ["vault", <FolderIcon key="v" className="size-4" />],
    ["decision", <GitBranchIcon key="d" className="size-4" />],
    ["output", <ArrowUpRightIcon key="o" className="size-4" />],
  ];

  return (
    <div className="relative h-full min-h-[420px] w-full overflow-hidden bg-[radial-gradient(circle_at_1px_1px,var(--tw-gradient-stops))] from-border/40 to-transparent bg-muted/5">
      {/* Left rail palette */}
      <div className="pointer-events-auto absolute left-3 top-1/2 z-10 flex -translate-y-1/2 flex-col gap-1.5 rounded-2xl border border-border/70 bg-background/90 p-1.5 shadow-lg backdrop-blur">
        {PALETTE.map(([kind, icon]) => {
          const label = canvasKindLabel(kind, t);
          return (
            <button
              key={kind}
              type="button"
              onClick={() => addNode(kind)}
              title={t("workflows.canvas.addKind", { kind: label })}
              className="flex w-16 flex-col items-center gap-1 rounded-xl px-2 py-2 text-[10px] font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              <span
                className={`flex size-7 items-center justify-center rounded-lg ${ACCENT[kind].tint}`}
              >
                {icon}
              </span>
              {label}
            </button>
          );
        })}
      </div>
      <InsertContext.Provider value={insertBetween}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStop={() => emit(nodes, edges)}
          onNodeClick={(_, n) => {
            // A trigger node has no in-canvas inspector — clicking it opens the Triggers
            // config panel (what people reach for) instead of silently selecting nothing.
            if (n.data?.kind === "trigger") {
              setSelectedId(null);
              editTriggers();
            } else {
              setSelectedId(n.id);
            }
          }}
          onPaneClick={() => setSelectedId(null)}
          onConnect={onConnect}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          // There was NO connection validation: you could join a node to itself, or hang two
          // edges off one route port, and the engine then had to guess.
          isValidConnection={(c) => {
            if (!c.source || !c.target) return false;
            if (c.source === c.target) return false;
            if (c.sourceHandle && c.targetHandle !== "dep") {
              const taken = edges.some(
                (e) => e.source === c.source && e.sourceHandle === c.sourceHandle,
              );
              if (taken) return false;
            }
            return true;
          }}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          proOptions={{ hideAttribution: true }}
          defaultEdgeOptions={{
            type: "insert",
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 14,
              height: 14,
              color: "var(--muted-foreground)",
            },
            style: { strokeWidth: 1.75, stroke: "var(--border)" },
          }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={18}
            size={1.5}
            className="!bg-transparent"
          />
        </ReactFlow>
      </InsertContext.Provider>
      {selectedId ? (
        <NodeInspector nodeId={selectedId} onClose={() => setSelectedId(null)} />
      ) : null}
    </div>
  );
}

export function WorkflowCanvas(props: {
  initial: WfGraph | null;
  seedTriggerLabels: string[];
  apps: WfApp[];
  vaultItems: ReadonlyArray<{ id: string; name: string }>;
  onEditTriggers: () => void;
  onChange: (g: WfGraph) => void;
}) {
  return (
    <ReactFlowProvider>
      <AppsContext.Provider value={props.apps}>
        <VaultContext.Provider value={props.vaultItems}>
          <EditTriggersContext.Provider value={props.onEditTriggers}>
            <CanvasInner
              initial={props.initial}
              seedTriggerLabels={props.seedTriggerLabels}
              onChange={props.onChange}
            />
          </EditTriggersContext.Provider>
        </VaultContext.Provider>
      </AppsContext.Provider>
    </ReactFlowProvider>
  );
}

/**
 * Routes editor — turns a decision step from a one-way gate into a step with N named exits.
 *
 * Each route becomes an output PORT on the card, so the author wires the arms up separately; an
 * arm dragged back to an earlier step is a loop, which is why the loop limit lives here too. The
 * old "loop the result back" checkbox on the End node promised this and never ran, so enabling
 * routes also clears it rather than leaving two contradictory controls.
 */
function RoutesEditor({
  nodeId,
  data,
  patch,
}: {
  nodeId: string;
  data: WfNodeData;
  patch: (p: Partial<WfNodeData>) => void;
}) {
  const { t } = useI18n();
  const edges = useStore((s) => s.edges);
  const nodes = useStore((s) => s.nodes);
  const { setEdges } = useReactFlow();
  const whenLabels = useConditionLabels();
  const routes = data.routes ?? [];
  const routing = hasRoutes(data.routes);

  // Which arms point back upstream — the author needs to see that a route loops, and the loop
  // limit only matters when one does.
  const loopTargets = new Map<string, string>();
  for (const e of edges) {
    if (e.source !== nodeId || !e.sourceHandle) continue;
    if (reachesForward(edges, e.target, nodeId)) {
      const target = nodes.find((n) => n.id === e.target) as Node<WfNodeData> | undefined;
      loopTargets.set(
        e.sourceHandle,
        target?.data.label?.trim() || t("workflows.canvas.routes.loopsTo"),
      );
    }
  }

  const write = (next: WfRoute[]) => patch({ routes: next });

  const enable = () =>
    patch({
      routes: defaultLoopRoutes(
        t("workflows.canvas.routes.moreDefault"),
        t("workflows.canvas.routes.doneDefault"),
      ),
      loopBack: false,
    });

  const disable = () => {
    // Dropping the routes also drops the arms, which no longer have ports to leave from.
    setEdges((es) => es.filter((e) => !(e.source === nodeId && e.sourceHandle)));
    patch({ routes: [] });
  };

  if (!routing) {
    return (
      <div className="flex flex-col gap-2">
        <button type="button" onClick={enable} className={`${BTN_SOFT} justify-center`}>
          <GitBranchIcon className="size-3.5" />
          {t("workflows.canvas.routes.enable")}
        </button>
        <p className="text-[10.5px] leading-snug text-muted-foreground">
          {t("workflows.canvas.routes.enableHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {t("workflows.canvas.routes.title")}
        </span>
        <button
          type="button"
          onClick={disable}
          className="text-[10px] font-medium text-muted-foreground hover:text-red-500"
        >
          {t("workflows.canvas.delete")}
        </button>
      </div>
      <p className="text-[10.5px] leading-snug text-muted-foreground">
        {t("workflows.canvas.routes.hint")}
      </p>

      {routes.map((route, i) => {
        const value = conditionValue(route.when);
        const takesValue = route.when.kind !== "always" && route.when.kind !== "hasMoreItems";
        const loopsTo = loopTargets.get(route.id);
        const set = (p: Partial<WfRoute>) =>
          write(routes.map((r, j) => (j === i ? { ...r, ...p } : r)));
        return (
          <div
            key={route.id}
            className="flex flex-col gap-1.5 rounded-lg border border-border/70 bg-card px-2.5 py-2"
          >
            <div className="flex items-center gap-1.5">
              <span className="min-w-0 flex-1 truncate text-[10px] font-bold uppercase tracking-wide text-primary/80">
                {route.id}
              </span>
              {loopsTo ? (
                <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-amber-500/15 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                  <RotateCwIcon className="size-2.5" />
                  {loopsTo}
                </span>
              ) : null}
              {routes.length > 1 ? (
                <button
                  type="button"
                  aria-label={t("workflows.canvas.routes.remove")}
                  onClick={() => {
                    setEdges((es) =>
                      es.filter((e) => !(e.source === nodeId && e.sourceHandle === route.id)),
                    );
                    write(routes.filter((_, j) => j !== i));
                  }}
                  className="shrink-0 text-muted-foreground/50 hover:text-red-500"
                >
                  <Trash2Icon className="size-3" />
                </button>
              ) : null}
            </div>
            <input
              className={FIELD}
              value={route.label ?? ""}
              placeholder={t("workflows.canvas.routes.labelPlaceholder")}
              onChange={(e) => set({ label: e.target.value })}
            />
            <select
              className={FIELD}
              value={route.when.kind}
              onChange={(e) =>
                set({ when: emptyCondition(e.target.value as WfRouteConditionKind) })
              }
            >
              {ROUTE_CONDITION_KINDS.map((k) => (
                <option key={k} value={k}>
                  {whenLabels[k]}
                </option>
              ))}
            </select>
            {takesValue ? (
              <input
                className={FIELD}
                value={value}
                placeholder={
                  route.when.kind === "ask"
                    ? t("workflows.canvas.routes.askPlaceholder")
                    : t("workflows.canvas.routes.valuePlaceholder")
                }
                onChange={(e) => set({ when: withConditionValue(route.when, e.target.value) })}
              />
            ) : null}
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => {
          const id = routeIdFromLabel(
            "route",
            routes.map((r) => r.id),
          );
          // New arms go ABOVE the trailing default, since the list is checked top to bottom and
          // an "anything else" arm placed first would swallow everything after it.
          const next = [...routes];
          const lastIsDefault = next.at(-1)?.when.kind === "always";
          next.splice(lastIsDefault ? next.length - 1 : next.length, 0, {
            id,
            label: "",
            when: emptyCondition("contains"),
          });
          write(next);
        }}
        className={`${BTN_SOFT} justify-center`}
      >
        <PlusIcon className="size-3.5" />
        {t("workflows.canvas.routes.add")}
      </button>

      {loopTargets.size > 0 ? (
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            {t("workflows.canvas.routes.maxPasses")}
          </span>
          <input
            className={FIELD}
            type="number"
            min={1}
            value={data.maxPasses ?? ""}
            placeholder="100"
            onChange={(e) => {
              const n = Number.parseInt(e.target.value, 10);
              patch({ maxPasses: Number.isFinite(n) && n >= 1 ? n : 100 });
            }}
          />
          <span className="text-[10.5px] leading-snug text-muted-foreground">
            {t("workflows.canvas.routes.maxPassesHint")}
          </span>
        </label>
      ) : null}
    </div>
  );
}

/** Can `from` reach `to` by following edges forward? Used to spot a route that loops back. */
function reachesForward(edges: ReadonlyArray<Edge>, from: string, to: string): boolean {
  const seen = new Set<string>([from]);
  const queue = [from];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (id === to) return true;
    for (const e of edges) {
      if (e.source !== id || seen.has(e.target)) continue;
      seen.add(e.target);
      queue.push(e.target);
    }
  }
  return false;
}
